/**
 * Formats a given date into "YYYY-MM-DD HH:MM" format.
 *
 * @param {Date} date - The date object to format.
 * @returns {string} The formatted date string in "YYYY-MM-DD HH:MM" format.
 * @example
 * const date = new Date("2024-01-29T15:45:00Z");
 * const result = formattedDatetime(date);
 * console.log(result); // Output: "2024-01-29 15:45"
 */
export const formattedDatetime = (date: Date) => {
    const parsedDate = date instanceof Date ? date : new Date(date);

    const year = parsedDate.getFullYear();
    const month = (parsedDate.getMonth() + 1).toString().padStart(2, "0");
    const day = parsedDate.getDate().toString().padStart(2, "0");
    const hours = parsedDate.getHours().toString().padStart(2, "0");
    const minutes = parsedDate.getMinutes().toString().padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * Formats a cost amount dynamically based on its magnitude:
 * - If the amount is >= 5, it keeps 1 decimal place.
 * - If the amount is between 1 and 5, it keeps 2 decimal places.
 * - If the amount is between 0.001 and 1, it keeps 4 decimal places.
 * - If the amount is less than 0.001, it keeps 5 decimal places.
 * - Integers are returned as-is.
 *
 * @param {number} amount - The raw numeric cost amount.
 * @returns {number} The formatted amount with appropriate decimal precision.
 *
 * @example
 * formattedCostAmount(123.456);   // 123.5
 * formattedCostAmount(2.3243);    // 2.32
 * formattedCostAmount(0.0456);    // 0.0456
 * formattedCostAmount(0.000456);  // 0.00046
 * formattedCostAmount(5);         // 5
 */
export const formattedKPIValue = (amount: number): number => {
    if (Number.isInteger(amount)) return amount;

    const absAmount = Math.abs(amount);

    // Very small numbers (< 0.001): use 5 decimals
    if (absAmount < 0.001) return Number(amount.toFixed(5));

    // Small fractional numbers (0.001–1): use 4 decimals
    if (absAmount < 1) return Number(amount.toFixed(4));

    // Medium values (1–5): use 2 decimals
    if (absAmount < 5) return Number(amount.toFixed(2));

    // Larger values: use 1 decimal
    return Number(amount.toFixed(1));
};

//NE NYÚLj HOZZÁ, ÍGY JÓ AHOGY VAN
/**
 * Cleans and formats a file path string.
 *
 * - Trims whitespace
 * - Replaces spaces with underscores
 * - Converts the string to lowercase
 * - Removes any invalid characters (only allows alphanumeric, underscores, periods, and `.pdf` extension)
 * - Removes any double slashes
 *
 * @param {string} filePath - The original file path string.
 * @returns {string} - The cleaned and formatted file path string.
 */
export function cleanFileName(filePath: string): string {
    // Step 1: Trim whitespace, replace inner spaces with underscores, make lowercase
    let formattedPath: string = filePath.trim().replace(/\s/g, "_").toLowerCase();

    // Step 2: Normalize Unicode (NFKD form splits accented chars into base + diacritic)
    // é -> e
    let normalized: string = formattedPath.normalize("NFKD");

    // Step 3: Remove all characters that are not:
    // - letters, numbers, or underscore (\w)
    // - forward slash (/)
    // - dot (.)
    normalized = normalized.replace(/[^\w\/.]/g, "");

    // Step 4: Return the sanitized result
    return normalized;
}

/**
 * Converts a Markdown-formatted string into a plain text string by removing Markdown formatting.
 *
 * @param {string} markdownString - The Markdown-formatted string to be converted.
 *
 * @example
 * const markdown = "**Bold** _Italic_ [Link](https://example.com)";
 * const result = createStringFromMarkdown(markdown);
 * console.log(result); // Output: "Bold Italic Link"
 *
 * @description
 * This function removes common Markdown formatting elements such as:
 * - Bold (`**text**`)
 * - Italic (`*text*`)
 * - Strikethrough (`~~text~~`)
 * - Inline code blocks (`\`code\``)
 * - Headers (`# Header`)
 * - Links (`[text](url)`)
 * - Images (`![alt text](url)`)
 * - Blockquotes (`> text`)
 * - Unordered list markers (`-`, `*`, `+`)
 * - Ordered list markers (`1. text`)
 */
export const createStringFromMarkdown = (markdownString: string) => {
    // Remove markdown formatting
    const stringFromMarkdown = markdownString
        .replace(/\*\*(.*?)\*\*/g, "$1") // Bold
        .replace(/\*(.*?)\*/g, "$1") // Italic
        .replace(/~~(.*?)~~/g, "$1") // Strikethrough
        .replace(/`(.*?)`/g, "$1") // Code
        .replace(/^#+\s/gm, "") // Headers
        .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Links
        .replace(/!\[.*?\]\(.*?\)/g, "") // Images
        .replace(/^>\s/gm, "") // Blockquotes
        .replace(/^\s*[-*+]\s/gm, "") // Unordered list markers
        .replace(/^\s*\d+\.\s/gm, "") // Ordered list markers
        .trim();

    return stringFromMarkdown;
};
